import { createContext, useContext, useMemo, useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { useTranslation } from "react-i18next";

import { useApp } from "../app";

import api from "libs/api";
import notificationSound from "assets/sounds/notification.mp3";
import { toast } from "react-toastify";

let audio = new Audio(notificationSound);

const TaskContext = createContext();

const TaskProvider = ({ children }) => {
  const { user, socket } = useApp();
  const { id } = useParams();
  const { t }  = useTranslation();

  const [task, setTask] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [file_progress, setFileProgress] = useState(null);
  const [task_close_confirmation, setTaskCloseConfirmation] = useState(false);

  useEffect(() => {
    (async function () {
      await getTaskData();
    })();
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    if (!!!socket) return;

    socket.emit("join_task", id);
    socket.on("task_update", (task) => {
      const last_user_id =
        task?.messages?.[task?.messages?.length - 1]?.user?._id;

      setTask({ ...task });

      if (user?._id !== last_user_id) audio.play();
    });

    return () => {
      if (!!!socket) return;

      socket.emit("task_leave", id);
    };

    // eslint-disable-next-line
  }, [socket]);

  const getTaskData = async (refetch = false) => {
    try {
      if (!!!refetch) {
        setLoading(true);
      }
      const task = await api.get(`/client/tasks/${id}`);
      setTask(task);
    } catch (err) {
      setError(true);
      toast.error(t("Task not found."));
    }

    setLoading(false);
  };

  const updateTask = (values) =>
    new Promise(async (resolve, reject) => {
      const previousTaskState = { ...task };

      try {
        setTask((prevTask) => ({
          ...prevTask,
          ...values,
        }));

        await api.put(`/client/tasks/${id}`, values);

        resolve();
      } catch (err) {
        setTask(previousTaskState);
        console.error(err);
        toast.error("Something went wrong.");
        reject(err);
      }
    });

  const sendMessage = async (text, files) => {
    const formData = new FormData();
    formData.append("text", text);
    files?.forEach((item) => {
      formData.append("files", item);
    });

    try {
      await axios.post(
        `${process.env.REACT_APP_API}/client/tasks/${id}/send-message`,
        formData,
        {
          withCredentials: true,
          onUploadProgress: ({ loaded, total }) => {
            const percent = Math.floor((loaded * 100) / total);
            setFileProgress(percent);
          },
        },
      );
    } catch (err) {
      console.error(err);
    }

    setTimeout(() => {
      setFileProgress(null);
    }, 200);
  };

  const editMessage = async (
    message_id,
    text,
    files,
    files_to_remove,
    is_optimistic,
  ) => {
    const task_fallback = task;
    const hasFiles = files?.length > 0 || files_to_remove?.length > 0;

    if (hasFiles) setFileProgress(0);

    try {
      const formData = new FormData();
      if (text !== "KEEP_SAME") {
        formData.append("text", text);

        if (is_optimistic) {
          setTask((prevTask) => {
            return {
              ...prevTask,
              messages: prevTask?.messages?.map((item) => {
                if (item._id === message_id) {
                  const currentFiles = item?.files ?? [];

                  return {
                    ...item,
                    text,
                    files: hasFiles
                      ? [
                          ...currentFiles.filter(
                            (file) => !files_to_remove.includes(file?.key),
                          ),
                          ...files,
                        ]
                      : item?.files,
                    updated_at: new Date().toISOString(),
                  };
                }
                return item;
              }),
            };
          });
        }
      }
      files_to_remove?.forEach((item) => {
        formData.append("files_to_remove", item);
      });
      files?.forEach((item) => {
        formData.append("files", item);
      });

      await axios.put(
        `${process.env.REACT_APP_API}/client/tasks/${id}/edit-message/${message_id}`,
        formData,
        {
          withCredentials: true,
          onUploadProgress: ({ loaded, total }) => {
            if (hasFiles) {
              const percent = Math.floor((loaded * 100) / total);
              setFileProgress(percent);
            }
          },
        },
      );

      getTaskData(true);
    } catch (err) {
      if (is_optimistic) setTask(task_fallback);
      console.error(err);
    }

    setTimeout(() => {
      setFileProgress(null);
    }, 200);
  };

  const value = useMemo(() => {
    return {
      data: task,
      file_progress,
      sendMessage,
      editMessage,
      loading,
      error,
      updateTask,
      task_close_confirmation,
      setTaskCloseConfirmation,
    };
    // eslint-disable-next-line
  }, [task, file_progress, loading, task_close_confirmation, error]);

  return <TaskContext.Provider value={value}>{children}</TaskContext.Provider>;
};

const useTask = () => useContext(TaskContext);
export { TaskContext, useTask };
export default TaskProvider;
